import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { CartItem } from '../../contexts/CartContext';
import DeliveryMap from './DeliveryMap';
import { formatEtaRange, DELIVERY_ZONES } from '../../utils/routeUtils';

type PaymentId = 'card' | 'cash';

interface CheckoutViewProps {
  items: CartItem[];
  totalPrice: number;
  onBack: () => void;
  onPlaceOrder: (details: OrderDetails) => void;
}

export interface OrderDetails {
  address: string;
  notes: string;
  paymentMethod: PaymentId;
  cashAmount?: number;
  changeAmount?: number;
}

const paymentOptions: { id: PaymentId; label: string; icon: string; description: string }[] = [
  { id: 'card', label: 'Credit / Debit Card', icon: 'ri-bank-card-line', description: 'Visa, Mastercard accepted' },
  { id: 'cash', label: 'Cash on Delivery', icon: 'ri-money-dollar-circle-line', description: 'Pay when order arrives' },
];

const DELIVERY_FEE = 0;

export default function CheckoutView({ items, totalPrice, onBack, onPlaceOrder }: CheckoutViewProps) {
  const { user } = useAuth();
  const [subStep, setSubStep] = useState<'delivery' | 'payment'>('delivery');
  const [address, setAddress] = useState(user?.address || '');
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentId | null>(null);
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [addressError, setAddressError] = useState('');
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [cashAmount, setCashAmount] = useState('');

  const formatPrice = (p: number) => `GYD $${p.toLocaleString()}`;
  const total = totalPrice + DELIVERY_FEE;

  const parsedCash = cashAmount ? parseFloat(cashAmount.replace(/[^\d.]/g, '')) : NaN;
  const change = !Number.isNaN(parsedCash) ? parsedCash - total : null;
  const insufficientCash = change !== null && change < 0;

  const handleDeliveryContinue = () => {
    if (!address.trim()) {
      setAddressError('Please enter your delivery address.');
      return;
    }
    setAddressError('');
    setSubStep('payment');
  };

  const handlePlaceOrder = () => {
    if (!paymentMethod) return;
    const details: OrderDetails = { address, notes, paymentMethod };
    if (paymentMethod === 'cash' && !Number.isNaN(parsedCash)) {
      details.cashAmount = parsedCash;
      details.changeAmount = change ?? undefined;
    }
    onPlaceOrder(details);
  };

  const formatCardNumber = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Step indicator */}
      <div className="px-6 pt-4 pb-3">
        <div className="flex items-center gap-2">
          {(['delivery', 'payment'] as const).map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  subStep === s
                    ? 'bg-[#FF6B35] text-white'
                    : i < (['delivery', 'payment'].indexOf(subStep))
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {i < (['delivery', 'payment'] as const).indexOf(subStep) ? (
                  <i className="ri-check-line text-xs" />
                ) : (
                  i + 1
                )}
              </div>
              <span className={`text-xs font-medium capitalize whitespace-nowrap ${subStep === s ? 'text-gray-900' : 'text-gray-400'}`}>
                {s === 'delivery' ? 'Delivery' : 'Payment'}
              </span>
              {i < 1 && <div className="h-px flex-1 bg-gray-200 mx-1" />}
            </div>
          ))}
        </div>
      </div>

      {/* Collapsible order summary */}
      <div className="px-6 mb-3">
        <button
          onClick={() => setSummaryOpen(!summaryOpen)}
          className="w-full flex items-center justify-between py-2.5 px-4 bg-[#FFF5F0] rounded-xl cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center text-[#FF6B35]">
              <i className="ri-shopping-bag-3-line text-sm" />
            </div>
            <span className="text-sm font-semibold text-gray-800">
              {items.length} item{items.length !== 1 ? 's' : ''} · {formatPrice(total)}
            </span>
          </div>
          <div className="w-4 h-4 flex items-center justify-center text-gray-500">
            <i className={`text-sm ${summaryOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
          </div>
        </button>
        {summaryOpen && (
          <div className="mt-2 bg-white border border-gray-100 rounded-xl px-4 py-3 space-y-2">
            {items.map((item) => (
              <div key={item.id} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 bg-gray-100 rounded px-1.5 py-0.5 font-medium">{item.quantity}×</span>
                  <span className="text-sm text-gray-700 truncate max-w-[160px]">{item.name}</span>
                </div>
                <span className="text-sm text-gray-700 font-medium whitespace-nowrap">{formatPrice(item.price * item.quantity)}</span>
              </div>
            ))}
            <div className="border-t border-gray-100 pt-2 flex justify-between text-sm font-bold text-gray-900">
              <span>Total</span>
              <span className="text-[#FF6B35]">{formatPrice(total)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6">

        {/* ── DELIVERY STEP ── */}
        {subStep === 'delivery' && (
          <div className="space-y-5 pb-4">
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-4">Delivery Details</h3>

              {user && (
                <div className="flex items-center gap-3 bg-[#fafaf8] border border-gray-100 rounded-xl px-4 py-3 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-[#FF6B35] to-[#e85d2a] text-white text-sm font-bold flex-shrink-0">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                    <p className="text-xs text-gray-400 truncate">{user.phone || user.email}</p>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Delivery Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-3 w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-map-pin-line text-sm" />
                    </div>
                    <textarea
                      value={address}
                      onChange={(e) => { setAddress(e.target.value); setAddressError(''); }}
                      placeholder="Enter your full delivery address in Georgetown..."
                      rows={3}
                      className={`w-full pl-9 pr-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] transition-all bg-[#fafaf8] text-gray-800 placeholder-gray-400 resize-none ${addressError ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                    />
                  </div>
                  {addressError && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line" />
                      {addressError}
                    </p>
                  )}

                  {/* Quick address suggestions */}
                  <div className="mt-3 space-y-2">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">Quick select area</p>
                    <div className="flex flex-wrap gap-2">
                      {['Providence, EBD', 'Diamond, EBD', 'Georgetown', 'Regent Street', 'Sheriff Street', 'East Coast'].map((area) => (
                        <button
                          key={area}
                          type="button"
                          onClick={() => { setAddress(area + ', Guyana'); setAddressError(''); }}
                          className="text-xs text-[#FF6B35] border border-[#FF6B35]/30 rounded-full px-3 py-1 hover:bg-[#FFF5F0] transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {area}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Delivery Notes <span className="text-gray-400 font-normal normal-case">(optional)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-3 w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-sticky-note-line text-sm" />
                    </div>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="E.g. Leave at gate, call on arrival..."
                      rows={2}
                      className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] transition-all bg-[#fafaf8] text-gray-800 placeholder-gray-400 resize-none"
                    />
                  </div>
                </div>

                {/* Delivery zones info — shown before address is typed */}
                {address.trim().length > 4 ? (
                  <DeliveryMap destination={address.trim()} compact className="mt-1" />
                ) : (
                  <div className="mt-2 space-y-1.5">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold mb-2">Delivery zones from Providence</p>
                    {DELIVERY_ZONES.map((zone) => {
                      const dotColors: Record<string, string> = {
                        green: 'bg-green-500', orange: 'bg-orange-400', yellow: 'bg-yellow-500',
                      };
                      const textColors: Record<string, string> = {
                        green: 'text-green-700', orange: 'text-orange-600', yellow: 'text-yellow-700',
                      };
                      return (
                        <div key={zone.name} className="flex items-center justify-between bg-[#fafaf8] border border-gray-100 rounded-xl px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColors[zone.color] ?? 'bg-gray-400'}`} />
                            <span className="text-xs text-gray-700 font-medium">{zone.label}</span>
                          </div>
                          <span className={`text-xs font-bold ${textColors[zone.color] ?? 'text-gray-500'}`}>
                            {zone.etaMin}–{zone.etaMax} min
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── PAYMENT STEP ── */}
        {subStep === 'payment' && (
          <div className="space-y-5 pb-4">
            <h3 className="text-base font-bold text-gray-900">Payment Method</h3>

            <div className="space-y-2">
              {paymentOptions.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setPaymentMethod(opt.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition-all cursor-pointer text-left ${
                    paymentMethod === opt.id
                      ? 'border-[#FF6B35] bg-[#FFF5F0]'
                      : 'border-gray-100 bg-white hover:border-gray-200'
                  }`}
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 ${paymentMethod === opt.id ? 'bg-[#FF6B35] text-white' : 'bg-gray-100 text-gray-500'}`}>
                    <i className={`${opt.icon} text-base`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${paymentMethod === opt.id ? 'text-[#FF6B35]' : 'text-gray-800'}`}>{opt.label}</p>
                    <p className="text-xs text-gray-400">{opt.description}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === opt.id ? 'border-[#FF6B35]' : 'border-gray-200'}`}>
                    {paymentMethod === opt.id && <div className="w-2.5 h-2.5 bg-[#FF6B35] rounded-full" />}
                  </div>
                </button>
              ))}
            </div>

            {/* Card form */}
            {paymentMethod === 'card' && (
              <div className="space-y-3 bg-[#fafaf8] rounded-xl p-4 border border-gray-100">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Card Details</p>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Card Number</label>
                  <input
                    type="text"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                    placeholder="0000 0000 0000 0000"
                    maxLength={19}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] transition-all bg-white text-gray-800 placeholder-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Cardholder Name</label>
                  <input
                    type="text"
                    value={cardName}
                    onChange={(e) => setCardName(e.target.value)}
                    placeholder="Name on card"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] transition-all bg-white text-gray-800 placeholder-gray-400"
                  />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Expiry</label>
                    <input
                      type="text"
                      value={cardExpiry}
                      onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                      placeholder="MM/YY"
                      maxLength={5}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] transition-all bg-white text-gray-800 placeholder-gray-400"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">CVV</label>
                    <input
                      type="text"
                      value={cardCvv}
                      onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="•••"
                      maxLength={4}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] transition-all bg-white text-gray-800 placeholder-gray-400"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-shield-check-line text-xs text-green-500" />
                  </div>
                  Your payment is secured with 256-bit SSL encryption
                </div>
              </div>
            )}

            {/* Cash on Delivery — change calculator */}
            {paymentMethod === 'cash' && (
              <div className="space-y-3 bg-[#fafaf8] rounded-xl p-4 border border-gray-100">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Troco / Change Calculator</p>

                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">
                    How much cash will you pay with?
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">GYD $</span>
                    <input
                      type="number"
                      min={0}
                      value={cashAmount}
                      onChange={(e) => setCashAmount(e.target.value)}
                      placeholder="0"
                      className={`w-full pl-14 pr-4 py-2.5 border rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 transition-all bg-white text-gray-800 placeholder-gray-400 ${
                        insufficientCash
                          ? 'border-red-300 focus:ring-red-200 focus:border-red-400'
                          : 'border-gray-200 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35]'
                      }`}
                    />
                  </div>
                </div>

                {/* Quick bill suggestions */}
                <div>
                  <p className="text-xs text-gray-400 mb-2">Quick select</p>
                  <div className="flex flex-wrap gap-2">
                    {[500, 1000, 2000, 5000, 10000].map((bill) => (
                      <button
                        key={bill}
                        type="button"
                        onClick={() => setCashAmount(String(bill))}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                          Number(cashAmount) === bill
                            ? 'bg-[#FF6B35] text-white border-[#FF6B35]'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-[#FF6B35]/50 hover:text-[#FF6B35]'
                        }`}
                      >
                        ${bill.toLocaleString()}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Change result */}
                {change !== null && (
                  <div className={`flex items-center justify-between rounded-lg px-4 py-3 ${
                    insufficientCash ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 flex items-center justify-center rounded-full ${insufficientCash ? 'bg-red-100' : 'bg-green-100'}`}>
                        <i className={`text-sm ${insufficientCash ? 'ri-error-warning-line text-red-500' : 'ri-coins-line text-green-600'}`} />
                      </div>
                      <span className={`text-xs font-semibold ${insufficientCash ? 'text-red-700' : 'text-green-700'}`}>
                        {insufficientCash ? 'Insufficient amount' : 'Your change'}
                      </span>
                    </div>
                    <span className={`text-base font-black ${insufficientCash ? 'text-red-600' : 'text-green-700'}`}>
                      {insufficientCash
                        ? `- GYD $${Math.abs(change).toLocaleString()}`
                        : `GYD $${change.toLocaleString()}`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Order total */}
            <div className="bg-white border border-gray-100 rounded-xl px-4 py-4 space-y-2">
              <div className="flex justify-between text-sm text-gray-500">
                <span>Subtotal</span>
                <span>{formatPrice(totalPrice)}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-500">
                <span>Delivery fee</span>
                <span className="text-green-600 font-semibold">Free</span>
              </div>
              <div className="border-t border-gray-100 pt-2 flex justify-between font-bold text-gray-900">
                <span>Total</span>
                <span className="text-[#FF6B35] text-base">{formatPrice(total)}</span>
              </div>
            </div>

            {/* Delivery address recap */}
            <div className="flex items-start gap-3 bg-[#fafaf8] border border-gray-100 rounded-xl px-4 py-3">
              <div className="w-4 h-4 flex items-center justify-center text-[#FF6B35] mt-0.5 flex-shrink-0">
                <i className="ri-map-pin-2-line text-sm" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Delivering to</p>
                <p className="text-sm text-gray-700 break-words">{address}</p>
              </div>
              <button
                type="button"
                onClick={() => setSubStep('delivery')}
                className="text-xs text-[#FF6B35] hover:underline cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                Edit
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div className="px-6 py-4 border-t border-gray-100 bg-white">
        {subStep === 'delivery' ? (
          <button
            onClick={handleDeliveryContinue}
            className="w-full bg-[#FF6B35] hover:bg-[#e85d2a] text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
          >
            Continue to Payment
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </button>
        ) : (
          <button
            onClick={handlePlaceOrder}
            disabled={!paymentMethod}
            className={`w-full font-bold py-3.5 rounded-full flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap ${
              paymentMethod
                ? 'bg-[#FF6B35] hover:bg-[#e85d2a] text-white'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-secure-payment-line" />
            </div>
            {paymentMethod ? `Place Order · ${formatPrice(total)}` : 'Select a payment method'}
          </button>
        )}
      </div>
    </div>
  );
}
